import {
  entryType,
  modeOfTransportation,
  actionCode, submissionType,
  cargoReleaseReasonCode
} from './options'

const cargoReleaseActionCode = [
  { value: 'A', description: 'Add' },
  { value: 'R', description: 'Replace' },
  { value: 'U', description: 'Update' },
  { value: 'D', description: 'Delete' },
]

// Reason code and replacement numbers only apply to Cargo Release deletion
const isCargoReleaseDelete = (formData) =>
  formData?.confirmation?.submissionType === 'CR' &&
  formData?.confirmation?.actionCode === 'D'

const entry = [
  {
    name: 'importerOfRecordNumber',
    label: 'Importer of Record Number',
    type: 'text',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '32-043479200',
    required: false,
    disabled: false,
    icon: undefined,
  },
  {
    name: 'filerCode',
    label: 'Filer Code',
    type: 'text',
    maxLength: 3,
    placeholder: '',
    defaultValue: 'SY2',
    required: false,
    disabled: false,
    icon: undefined,
  },
  {
    name: 'entryNumber',
    label: 'Entry Number',
    type: 'text',
    maxLength: 8,
    placeholder: '',
    defaultValue: '',
    required: false,
    disabled: false,
    icon: undefined,
  },
  {
    name: 'entryType',
    label: 'Entry Type',
    type: 'auto',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '01',
    required: false,
    disabled: false,
    icon: undefined,
  },
  {
    name: 'transportationMode',
    label: 'Mode of Transportation',
    type: 'auto',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '11',
    required: false,
    disabled: false,
    icon: undefined,
  },
  {
    name: 'entryPort',
    label: 'Port of Entry',
    type: 'text',
    maxLength: 4,
    placeholder: '',
    defaultValue: '4601',
    required: false,
    disabled: false,
    icon: undefined,
  },
  {
    name: 'arrivalDate',
    label: 'Arrival Date',
    type: 'date',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '',
    required: false,
    disabled: false,
    icon: undefined,
  },
  {
    name: 'importDate',
    label: 'Date of Import',
    type: 'date',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '2025-05-07',
    required: false,
    disabled: false,
    icon: undefined,
  },
]

const amount = [
  {
    name: 'totalAmount',
    label: 'Total Amount',
    type: 'number',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '',
    required: false,
    disabled: false,
    icon: undefined,
    readOnly: true,
  },
  {
    name: 'statementDate',
    label: 'Statement Date',
    type: 'date',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '2025-05-19',
    required: false,
    disabled: false,
    icon: undefined,
    readOnly: true,
  },
  {
    type: 'new',
  },
  {
    name: 'totalDuty',
    label: 'Total Duty',
    type: 'number',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '',
    required: false,
    disabled: false,
    icon: undefined,
    readOnly: true,
  },
  {
    name: 'totalMpf',
    label: 'Total MPF',
    type: 'number',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '',
    required: false,
    disabled: false,
    icon: undefined,
    readOnly: true,
  },
  {
    name: 'totalHmf',
    label: 'Total HMF',
    type: 'number',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '',
    required: false,
    disabled: false,
    icon: undefined,
    readOnly: true,
  },
  {
    name: 'totalOtherFee',
    label: 'Total Other Fee',
    type: 'number',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '',
    required: false,
    disabled: false,
    icon: undefined,
    readOnly: true,
  },
  {
    name: 'totalTaxes',
    label: 'Total Taxes',
    type: 'number',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '',
    required: false,
    disabled: false,
    icon: undefined,
    readOnly: true,
  },
]

const government = [
  {
    name: 'fdaRequired',
    label: 'FDA (Required)',
    type: 'text',
    maxLength: 1,
    placeholder: '',
    defaultValue: '1',
    required: false,
    disabled: false,
    icon: undefined,
  },
  {
    name: 'fdaEntered',
    label: 'FDA (Entered)',
    type: 'text',
    maxLength: 1,
    placeholder: '',
    defaultValue: '1',
    required: false,
    disabled: false,
    icon: undefined,
  },
  {
    name: 'epaRequired',
    label: 'EPA (Required)',
    type: 'text',
    maxLength: 1,
    placeholder: '',
    defaultValue: '0',
    required: false,
    disabled: false,
    icon: undefined,
  },
  {
    name: 'epaEntered',
    label: 'EPA (Entered)',
    type: 'text',
    maxLength: 1,
    placeholder: '',
    defaultValue: '0',
    required: false,
    disabled: false,
    icon: undefined,
  },
]

const submission = [
  {
    name: 'submissionType',
    label: 'Submission Type',
    type: 'select',
    defaultValue: 'ES',
    sizeProp: { xs: 12, sm: 6 }
  }, {
    name: 'actionCode',
    label: 'Action Code',
    type: 'auto',
    maxLength: undefined,
    placeholder: '',
    defaultValue: '',
    required: true,
    disabled: false,
    icon: undefined,
    condition: (formData) => !['PGA', 'SU'].includes(formData?.confirmation?.submissionType),
    computedOptions: (formData) => {
      const type = formData?.confirmation?.submissionType
      if (type === 'CR') return cargoReleaseActionCode
      return actionCode.filter(opt => opt.value !== 'M')
    },
    sizeProp: { xs: 12, sm: 6 }
  },

  // Entry Summary submission fields
  {
    name: 'excludeStatement',
    label: 'Exclude Statement',
    type: 'checkbox',
    condition: (formData) => formData?.confirmation?.submissionType === 'ES'
  },

  // Cargo Release submission fields
  {
    name: 'reasonCode',
    label: 'Reason Code',
    type: 'auto',
    defaultValue: '',
    required: true,
    disabled: false,
    condition: isCargoReleaseDelete,
    sizeProp: { xs: 12, sm: 6 }
  },
  {
    name: 'replacementInBondNumber',
    label: 'Replacement In-Bond Number',
    type: 'text',
    maxLength: undefined,
    defaultValue: '',
    required: false,
    disabled: false,
    condition: (formData) =>
      isCargoReleaseDelete(formData) &&
      formData?.confirmation?.reasonCode === '02',
    sizeProp: { xs: 12, sm: 6 }
  },
  {
    name: 'replacementEntryNumber',
    label: 'Replacement Entry Number',
    type: 'text',
    maxLength: undefined,
    defaultValue: '',
    required: false,
    disabled: false,
    condition: (formData) =>
      isCargoReleaseDelete(formData) &&
      formData?.confirmation?.reasonCode === '03',
    sizeProp: { xs: 12, sm: 6 }
  },
  {
    name: 'replacementFtzAdmissionNumber',
    label: 'Replacement FTZ Admission Number',
    type: 'text',
    maxLength: undefined,
    defaultValue: '',
    required: false,
    disabled: false,
    condition: (formData) =>
      isCargoReleaseDelete(formData) &&
      formData?.confirmation?.reasonCode === '04',
    sizeProp: { xs: 12, sm: 6 }
  },
  // Statement Update fields
  {
    name: 'clientBranch',
    label: 'Client Branch Designation',
    type: 'text',
    maxLength: 2,
    condition: (formData) => formData?.confirmation?.submissionType === 'SU'
  },
]

const optionsMap = {
  entryType: entryType,
  transportationMode: modeOfTransportation,
  submissionType,
  actionCode: actionCode.filter(opt => opt.value !== 'M'),
  reasonCode: cargoReleaseReasonCode
}

export {
  entry,
  amount,
  government,
  submission,
  optionsMap
}